import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Fade, Typography } from '@mui/material';
import { useTranslation } from '../contexts/TranslationContext';

interface RagKGAppProps {
  /**
   * RagKGApp wrapper component
   * This component loads the RagKGApp from its bundled build output.
   * The assets are served as static files from `public/dist`.
   */
  hashPath?: string;
  /** When true, shows an overlay with loading animation until the iframe document has loaded. */
  showPreloader?: boolean;
}

/**
 * RagKGApp wrapper component
 * This component loads the RagKGApp from its bundled build output.
 * The assets are served as static files from `public/dist`.
 */
const RagKGApp: React.FC<RagKGAppProps> = ({ hashPath, showPreloader = true }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { t } = useTranslation();
  const [iframeDocumentLoaded, setIframeDocumentLoaded] = useState(false);

  const iframeSrc = useMemo(() => {
    const basePath = `/dist/index.html?v=${__APP_BUILD_TIME__}`;
    if (!hashPath) {
      return basePath;
    }

    const normalizedHash = hashPath.startsWith('/') ? hashPath : `/${hashPath}`;
    return `${basePath}#${normalizedHash}`;
  }, [hashPath]);

  useEffect(() => {
    setIframeDocumentLoaded(false);
  }, [iframeSrc]);

  const handleIframeLoad = () => {
    setIframeDocumentLoaded(true);
  };

  const handleIframeError = () => {
    setIframeDocumentLoaded(true);
  };

  const showOverlay = showPreloader && !iframeDocumentLoaded;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 200,
      }}
    >
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        title="RagKGApp"
        allow="fullscreen"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
      <Fade in={showOverlay} timeout={280} unmountOnExit>
        <Box
          aria-busy="true"
          aria-live="polite"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(18, 18, 18, 0.92)'
                : 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(4px)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <CircularProgress
            size={52}
            thickness={4}
            sx={{
              '@keyframes ragKgPulse': {
                '0%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.72, transform: 'scale(0.96)' },
                '100%': { opacity: 1, transform: 'scale(1)' },
              },
              animation: 'ragKgPulse 1.4s ease-in-out infinite',
            }}
          />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              '@keyframes ragKgTextPulse': {
                '0%, 100%': { opacity: 0.85 },
                '50%': { opacity: 0.45 },
              },
              animation: 'ragKgTextPulse 1.6s ease-in-out infinite',
            }}
          >
            {t('common.loading')}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default RagKGApp;


